import { FullStory } from '@fullstory/browser'
import * as Sentry from '@sentry/vue'

import { handleError } from '@utils/helpers'
import ax from '@utils/axios'
import { useGeneralStore } from '@stores/general'
import { useAuthStore } from '@stores/auth'
import { router } from '@/router'

async function fetchMe(ignoreGlobalCatch = false) {
  const generalStore = useGeneralStore()
  const authStore = useAuthStore()

  try {
    generalStore.showGlobalLoadingIndicator = true
    const { data } = await ax.get('/v2/users/me', {
      validateStatus: function(status) {
        if (status === 302) { // TODO: this is not valid anymore?
          window.location = 'https://relevize.com/tos-accept'
          return true
        }
        return status === 200
      },
      ...(ignoreGlobalCatch ? { skipAuthRefresh: true, ignoreGlobalCatch: true } : {}),
    })

    this.user = data
    this.isAdmin = data.is_admin
    this.isImpersonating = data.is_impersonating

    Sentry.setUser({ id: data.id, email: data.email })
    Sentry.setTag('userType', data.role)
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Authenticated user ' + data.email,
      level: 'info',
    })

    if (!this.user?.has_accepted_tos) {
      router.push({ name: 'terms' })
    }

    if (!data.is_admin && import.meta.env.MODE !== 'development') {
      // Setup Fullstory
      FullStory('setIdentity', {
        uid: this.user.id,
        properties: {
          displayName: this.user.name,
          email: this.user.email,        
        }
      })
    }

    // TODO: the vendor and partner api calls should probably go somewhere else
    // where we can ensure the data comes back before it re-routes to the campaigns page
    if (data.vendor_id) {
      this.vendorId = data.vendor_id
      await this.fetchVendor(data.vendor_id)
    }
    else if (data.partner_id) {
      this.partnerId = data.partner_id
      await this.fetchPartner(data.partner_id)
    }
    else {
      // this means the user is not attached to a vendor or partner
      // this condition should never happen
      // TODO: this should show an error page
      authStore.logOut()
      throw new Error('User not a vendor or a partner.')
    }

    return data
  }
  catch(error) {
    handleError(error)
  }
  finally {
    generalStore.showGlobalLoadingIndicator = false
  }
}

async function acceptTerms() {
  const generalStore = useGeneralStore()

  try {
    generalStore.showGlobalLoadingIndicator = true
    const { data } = await ax.post('/v2/users/me/accept-terms')

    this.user = {
      ...data,
      has_accepted_tos: true,
    }
    router.push({ name: 'campaigns' })
  }
  catch(error) {
    handleError(error)
  }
  finally {
    generalStore.showGlobalLoadingIndicator = false
  }
}

async function fetchPartner(partnerId) {
  try {
    const response = await ax.get(`/v2/partners/${partnerId}`)
    this.partner = response.data
    this.connectors = response.data.connectors
  }
  catch(error) {
    handleError(error)
  }
}

async function fetchVendor(vendorId) {
  try {
    const response = await ax.get(`/v2/vendors/${vendorId}`)
    this.vendor = response.data
    this.connectors = response.data.connectors
  }
  catch(error) {
    handleError(error)
  }
}

function updateSettings({ key, val }) {
  this.settings[key] = val
}

async function updatePartnerSettings(payload) {
  const generalStore = useGeneralStore()

  try {
    generalStore.showLocalLoadingIndicator = true
    const response = await ax.patch(`/v2/partners/${this.partner.id}`, payload)
    this.partner = response.data
  }
  catch(error) {
    handleError(error)
    throw error
  }
  finally {
    generalStore.showLocalLoadingIndicator = false
  }
}

async function updateDefaultSavedAudience(elementsByTitle) {
  // TODO: deprecated?
  const generalStore = useGeneralStore()
  generalStore.showLocalLoadingIndicator = true

  // update the audience element assns for the partner's default saved audience
  let inclusions = elementsByTitle['Profile Locations'].concat(elementsByTitle['Company Size'])
  // eslint-disable-next-line
  inclusions = inclusions.map(({ id, label, ...item }) => item)
  let exclusions = elementsByTitle.Industry.concat(elementsByTitle['Company Name'])
  // eslint-disable-next-line
  exclusions = exclusions.map(({ id, label, ...item }) => item)

  try {
    const response = await ax.put(`/v2/audiences/${this.partner.default_audience.id}`, {
      linkedin_included: inclusions,
      linkedin_excluded: exclusions
    })
    this.partner.default_audience = response.data
  }
  catch(error) {
    handleError(error)
  }
  finally {
    generalStore.showLocalLoadingIndicator = false
  }
}

function storeCreativeUrl(data) {
  this.partner.creatives_by_template_id[data.creative_template_id] = data
}

async function inviteUser({ email }) {
  try {
    const response = await ax.put(`/v2/partners/${this.partner.id}/users`, { users: [{ email: email }] })
    this.partner.users = response.data
    return { success: true }
  }
  catch(error) {
    handleError(error)
    return { success: false, error: error.response?.data?.message || 'An error occurred while inviting the user.' }
  }
}

async function deleteUser(userId) {
  try {
    if (this.partner.id) {
      await ax.delete(`/v2/partners/${this.partner.id}/users/${userId}`)
      this.partner.users = this.partner.users.filter(user => user.id !== userId)
    } else if (this.vendor.id) {
      await ax.delete(`/v2/vendors/${this.vendor.id}/users/${userId}`)
      this.vendor.users = this.vendor.users.filter(user => user.id !== userId)
    }
  }
  catch(error) {
    handleError(error)
  }
}

async function fetchVendorUsers(vendorId) {
  try {
    const response = await ax.get(`/v2/vendors/${vendorId}/users`)
    this.vendor.users = response.data
  }
  catch(error) {
    handleError(error)
  }
}

async function fetchPartnerVendorAssociations() {
  try {
    const response = await ax.get(`/v2/partners/${this.partner.id}/vendors`)
    this.partner.vendorAssociations = response.data
  } catch (error) {
    handleError(error)
    throw error
  }
}

// invite vendor co-workers to Relevize app
async function inviteVendorUser({ new_user_email }) {
  try {
    const response = await ax.post(`/v2/vendors/${this.vendor.id}/users`, { new_user_email })
    this.vendor.users = response.data
    return { success: true }
  }
  catch(error) {
    handleError(error)
    return { success: false, error: error.response?.data?.message || 'An error occurred while inviting the user.' }
  }
}

async function saveUserName(newName) {
  try {
    await ax.post('/v2/users/me/update-name',
      {
        name: newName
      }) 
    return true
  }
  catch(error) {
    handleError(error)
  } finally {
    /* fetch user data after successful name update in user-settings.vue,
    so that the user store is up-to-date with the new name */
    this.fetchMe()
  }
}

async function saveTimezone(newTimezone) {
  try {
    await ax.patch('/v2/users/me/timezone',
      {
        timezone: newTimezone
      }) 
    return true
  }
  catch(error) {
    handleError(error)
  } finally {
    /* fetch user data after successful name update in user-settings.vue,
    so that the user store is up-to-date with the new name */
    this.fetchMe()
  }
}

async function getNotificationSettings() {
  try {
    const response = await ax.get('/v2/notifications/settings')
    this.notificationSettings = response.data
  }
  catch(error) {
    handleError(error)
  }
}

async function getLinkedinAccountStatus(refresh_status = null) {
  // Set noLinkedinAccountConnected if the user has no account connected
  try {
    const response = await ax.get('/v2/users/me/linkedin-account', {
      params: {
        refresh_status: refresh_status
      }
    })
    this.linkedinAccountStatus = response.data
  } catch (error) {
    if (error.response && error.response.status === 404) {
      this.linkedinAccountStatus.noLinkedinAccountConnected = true
    } else {
      handleError(error)
    }
  }
}

async function disconnectLinkedinAccount() {
  try {
    await ax.delete('/v2/users/me/linkedin-account')
  } catch (error) {
    handleError(error)
  }
}

async function getLinkedinOAuthURL() {
  try {
    const response = await ax.get('/v2/users/me/linkedin-account/onboarding-link')
    return response.data
  } catch (error) {
    handleError(error)
    return null
  }
}

async function createNotificationSetting(obj) {
  try {
    const data = JSON.stringify(obj)
    const response = await ax.post('/v2/notifications/settings', data)
    return response.data
  }
  catch(error) {
    handleError(error)
  }
}

async function updateNotificationSetting(obj) {
  try {
    const data = JSON.stringify(obj)
    const response = await ax.put('/v2/notifications/settings', data)
    return response.data
  }
  catch(error) {
    handleError(error)
  }
}

function userHasAnyConnectors() {
  const connectors = this.getUserRole === 'partner' ? this.partner.connectors : this.vendor.connectors
  return connectors.length > 0
}

async function fetchUserNylasSignature() {
  this.signatureIsLoading = true
  try {
    const response = await ax.get('/v2/email-signatures/me')
    this.userNylasSignature = response.data
  } catch (error) {
    if (error.response.status !== 404) {
      handleError(error)
    }
  }
  this.signatureIsLoading = false
}

async function saveUserNylasSignature(parameters = { body: null }) {
  const { body } = parameters

  this.signatureIsLoading = true
  try {
    const data = {
      body,
    }
    const response = await ax.post('/v2/email-signatures/me/update', data)
    this.userNylasSignature = response.data
  } catch (error) {
    handleError(error)
  }
  this.signatureIsLoading = false
  this.fetchMe()
}

async function updatePartnerIgnoredNotificationList({ partnerIds }) {
  try {
    const response = await ax.put('/v2/notifications/partner-leads', {
      partner_ids: partnerIds
    })
    return response.data
  }
  catch(error) {
    handleError(error)
  }
}

async function updateLeadNotificationsAll(enable_lead_notification_emails) {
  try {
    const response = await ax.put('/v2/notifications/lead-notifications-all', {
      enable_lead_notification_emails
    })
    return response.data
  }
  catch(error) {
    handleError(error)
  }
}

function updateVendorPortalBranding({ hex_color = null, logo = null }) {
  if (!this.vendorId) {
    return
  }
  if(hex_color) {
    this.vendor.hex_color = hex_color
  }
  if (logo) {
    if(this.vendor.media_library_logo_href) {
      URL.revokeObjectURL(this.vendor.media_library_logo_href)
    }
    this.vendor.media_library_logo_href = URL.createObjectURL(logo)
  }
}

function resetVendorPortalBranding() {
  if (!this.vendorId) {
    return
  }

  this.vendor.hex_color = null
  if(this.vendor.media_library_logo_href) {
    URL.revokeObjectURL(this.vendor.media_library_logo_href)
  }
  this.vendor.media_library_logo_href = null
}

async function updateTableColumns(columns) {
  try {
    const response = await ax.put('/v2/users/view_settings', {
      view_settings: columns
    })
    return response.data
  } catch (error) {
    handleError(error)
    throw error
  }
}

export default {
  fetchMe,
  acceptTerms,
  fetchPartner,
  fetchVendor,
  updateSettings,
  updateDefaultSavedAudience,
  updatePartnerSettings,
  storeCreativeUrl,
  inviteUser,
  inviteVendorUser,
  deleteUser,
  saveUserName,
  saveTimezone,
  getNotificationSettings,
  getLinkedinAccountStatus,
  createNotificationSetting,
  getLinkedinOAuthURL,
  updateNotificationSetting,
  fetchVendorUsers,
  fetchPartnerVendorAssociations,
  userHasAnyConnectors,
  fetchUserNylasSignature,
  saveUserNylasSignature,
  disconnectLinkedinAccount,
  updatePartnerIgnoredNotificationList,
  updateVendorPortalBranding,
  resetVendorPortalBranding,
  updateLeadNotificationsAll,
  updateTableColumns,
}
